import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSImage, HHDSImageEvent, HHDSImageTagName } from '../Image/Image';
import gsap, { Cubic } from 'gsap';
import { PageEvent } from '../../common/page-lifecycle/PageEvent';
import { ScrollTrigger } from 'gsap/all';
import { HHDSVideoDirect, HHDSVideoDirectAttrNames, HHDSVideoDirectTagName } from '../VideoDirect/VideoDirect';
import { HHDSVideoEvent } from '../../utils/VideoTypes';
import { HHDSHeroMediaType } from '../Hero/Hero';

const DEBUG_VERBOSE: boolean = false;
export const HHDSMastheadTagName: string = 'hhds-masthead';
const TAG_NAME: string = HHDSMastheadTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export enum HHDSMastheadMediaType {
	video = 'video',
	image = 'image',
}

export enum HHDSMastheadColorMode {
	light = 'light',
	dark = 'dark',
}

export const HHDSMastheadAttrNames = {
	mediaType: 'media-type',
	header: 'header',
	eyebrow: 'eyebrow',
	subtitle: 'subtitle',
	cover: 'cover',
	src: 'src',
	srcSet: 'srcset',
	sizes: 'sizes',
	mode: 'mode',
	showScrollButton: 'show-scroll-button',
	alt: 'alt',
	parallax: 'parallax',
	force16x9: 'force-16x9',
};

const Attrs = HHDSMastheadAttrNames;

enum ParallaxType {
	windowHeight = 'window-height',
	imageHeight = 'image-height',
}

export class HHDSMasthead extends Component {
	private parallaxTween: gsap.core.Tween | null = null;
	private elementTweens: gsap.core.Tween[] = [];
	private refreshFunc!: () => void;
	private parallaxType: ParallaxType | null = null;
	private video: HHDSVideoDirect | null = null;
	private resizeFunc: any;

	constructor() {
		super();
		this.refreshFunc = this.onScrollTriggerRefresh.bind(this);
		this.resizeFunc = () => this.updateAutoCover('resize');
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const colorMode: HHDSMastheadColorMode = this.vars.get<HHDSMastheadColorMode>(Attrs.mode);

		if (colorMode == HHDSMastheadColorMode.light) {
			this.style.setProperty('--masthead-header-color', 'var(--color-primary-coconut)');
			this.style.setProperty('--masthead-subtitle-color', 'var(--color-tint-coconut-70-a11y)');
			this.style.setProperty('--masthead-surface', 'var(--color-primary-sand)');
		} else {
			this.style.setProperty('--masthead-header-color', 'var(--color-primary-sand)');
			this.style.setProperty('--masthead-subtitle-color', 'var(--color-tint-sand-70)');
			this.style.setProperty('--masthead-surface', 'var(--color-primary-coconut-dark)');
		}

		const scrollDownButtonHtml = this.htmlIf(Attrs.showScrollButton, () => {
			return `<hhds-button-hero class="${TAG_NAME}__button" mode="${colorMode}"></hhds-button-hero>`;
		});
		const eyebrowHtml = this.htmlIfVar(Attrs.eyebrow, (x) => {
			return `<h5 class="${TAG_NAME}__eyebrow heading heading--05">${x}</h5>`;
		});
		const headerHtml = this.htmlIfVar(Attrs.header, (x) => {
			return `<h2 class="${TAG_NAME}__header heading heading--01">${x.toUpperCase()}</h2>`;
		});
		const subtitleHtml = this.htmlIfVar(Attrs.subtitle, (x) => {
			return `<p class="${TAG_NAME}__subtitle paragraph">${x}</p>`;
		});

		const mediaType: HHDSMastheadMediaType = this.vars.get<HHDSMastheadMediaType>(Attrs.mediaType);
		const mediaHtml: string = this.getMediaHtml(mediaType);

		window.addEventListener('resize', this.resizeFunc);

		let contentLayoutClass: string = '';
		if (this.vars.get<string>(Attrs.subtitle)?.length) {
			contentLayoutClass = `${TAG_NAME}__content--min-height`;
		} else {
			contentLayoutClass = `${TAG_NAME}__content--bottom-padding`;
		}

		const html: string = `
      <div class="${TAG_NAME}">
				<div class="container">
					<div class="grid">
						<div class="col-span-4 md:col-span-8 md:col-start-3 lg:col-span-10 lg:col-start-8 ${TAG_NAME}__content ${contentLayoutClass}">
							${eyebrowHtml}
							${headerHtml}
							${subtitleHtml}
							${scrollDownButtonHtml}
						</div>
					</div>
				</div>
				<div class="${TAG_NAME}__bottom-anchor"></div>
				${mediaHtml}
      </div>
    `;

		this.setInnerHtml('', '', html);

		this.video = this.root.querySelector(HHDSVideoDirectTagName) as HHDSVideoDirect;
		this.video?.addEventListener(HHDSVideoEvent.coverLoaded, () => {
			this.updateAutoCover('cover loaded');
		});
		this.video?.addEventListener(HHDSVideoEvent.loaded, () => {
			this.updateAutoCover('video loaded');
		});

		const bottomAnchor = this.element(`.${TAG_NAME}__bottom-anchor`) as HTMLElement;
		if (bottomAnchor) {
			const scrollButton = this.element('hhds-button-hero');
			scrollButton?.addEventListener('click', () => {
				const rect = bottomAnchor.getBoundingClientRect();
				const yPos = rect.top + window.scrollY;
				window.scrollTo({ top: yPos, behavior: 'smooth' });
			});
		}

		document.addEventListener(PageEvent.enter, () => {
			if (!this.initialised) return; // Ignore if component is de-initialised by being on a leaving page
			this.prepareElementsForAnimation();
			this.animateElementsIn();
		});
		document.addEventListener(PageEvent.enterComplete, () => {
			if (!this.initialised) return; // Ignore if component is de-initialised by being on a leaving page
			//this.animateElementsIn();
		});
		document.addEventListener(PageEvent.leave, () => {
			this.elementTweens.forEach((tween) => tween.kill());
			this.elementTweens = [];
		});

		const image: HHDSImage = this.element<HHDSImage>(HHDSImageTagName);
		image?.addEventListener(HHDSImageEvent.load, () => {
			const useParallax: boolean = this.vars.get<boolean>(Attrs.parallax);
			if (useParallax) this.configureParallax();
		});

		ScrollTrigger.addEventListener('refresh', this.refreshFunc);
		//window.addEventListener('resize', this.resizeFunc);
	}

	private updateAutoCover(_reason: string): void {
		if (!this.initialised) return;
		switch (this.vars.get<HHDSHeroMediaType>(Attrs.mediaType)) {
			case HHDSHeroMediaType.video:
				const parentElement = this.root.querySelector(`.${TAG_NAME}__video-container`) as HTMLElement;
				const parentRatio = parentElement.clientWidth / parentElement.clientHeight;
				this.updateAutoCover_video(parentRatio);
				break;
			case HHDSHeroMediaType.image:
				//this.updateAutoCover_image(parentRatio);
				break;
		}
	}

	private updateAutoCover_video(parentRatio: number): void {
		const videoDirect = this.root.querySelector(HHDSVideoDirectTagName) as HHDSVideoDirect;
		const videoElement = videoDirect.videoElement;
		let mediaRatio = 1920 / 1080;
		const coverElement = videoDirect.coverElement;

		videoDirect.componentElement.style.width = '100%';
		videoDirect.style.width = '100%';
		videoDirect.componentElement.style.height = '100%';
		videoDirect.style.height = '100%';

		if (videoElement?.videoWidth) {
			mediaRatio = videoElement.videoWidth / videoElement.videoHeight;
		} else if (coverElement?.clientWidth) {
			mediaRatio = coverElement.clientWidth / coverElement.clientHeight;
		}
		if (mediaRatio >= parentRatio) {
			// Fit to height
			// Reset other styles used by fit-to-width
			if (videoElement) {
				videoElement.style.minWidth = 'unset';
				videoElement.style.minHeight = 'unset';
			}
			// Apply fit-to-height styles
			if (videoElement) {
				videoElement.style.maxHeight = '100%';
				videoElement.style.width = 'auto';
				videoElement.style.height = '100%';
			}
		} else {
			// Fit to width
			// Reset styles used by fit-to-height
			if (videoElement) videoElement.style.maxHeight = 'unset';
			// Apply fit-to-width styles
			if (videoElement) {
				videoElement.style.width = '100%';
				videoElement.style.height = 'auto';
				videoElement.style.minWidth = '100%';
				videoElement.style.minHeight = '100%';
			}
		}
	}

	private getMediaHtml(type: HHDSMastheadMediaType): string {
		switch (type) {
			case HHDSMastheadMediaType.video:
				if (!this.vars.get<string>(Attrs.src)?.length) {
					return '';
				}
				let attributes = {
					[HHDSVideoDirectAttrNames.src]: this.vars.get<string>(Attrs.src),
					[HHDSVideoDirectAttrNames.cover]: this.vars.get<string>(Attrs.cover),
					[HHDSVideoDirectAttrNames.autoplay]: true,
					[HHDSVideoDirectAttrNames.loop]: true,
					[HHDSVideoDirectAttrNames.controls]: false,
				};
				let attributesString: string = '';
				Object.keys(attributes).forEach((key) => (attributesString += `${key}="${attributes[key]}" `));
				const style = `--video-component-width: auto;`;
				let videoHtml: string = `<${HHDSVideoDirectTagName} id="main" style="${style}" ${attributesString}></${HHDSVideoDirectTagName}>`;
				return `<div class="${TAG_NAME}__video-container ${this.getRatioClass(type)}">${videoHtml}</div>`;
			case HHDSMastheadMediaType.image:
				const src = this.vars.get<string>(Attrs.src);
				const srcset = this.vars.get<string>(Attrs.srcSet);
				const sizes = this.vars.get<string>(Attrs.sizes);
				const alt = this.vars.get<string>(Attrs.alt);
				let inner: string;
				if (srcset?.length && sizes?.length) {
					inner = `<${HHDSImageTagName} srcset="${srcset}" sizes="${sizes}" alt="${alt}"></${HHDSImageTagName}>`;
				} else if (src?.length) {
					inner = `<${HHDSImageTagName} src="${src}" alt="${alt}"></${HHDSImageTagName}>`;
				} else {
					return '';
				}
				return `<div class="${TAG_NAME}__image-container ${this.getRatioClass(type)}">${inner}</div>`;
		}
	}

	private getRatioClass(type: HHDSMastheadMediaType): string {
		const force16x9: boolean = this.vars.get<boolean>(Attrs.force16x9);
		switch (type) {
			case HHDSMastheadMediaType.video:
				return force16x9 ? `${TAG_NAME}__video-container-16x9` : `${TAG_NAME}__video-container-multiratio`;
			case HHDSMastheadMediaType.image:
				return force16x9 ? `${TAG_NAME}__image-container-16x9` : `${TAG_NAME}__image-container-multiratio`;
		}
	}

	private prepareElementsForAnimation(): void {
		const eyebrow = this.element(`.${TAG_NAME}__eyebrow`);
		const header = this.element(`.${TAG_NAME}__header`);
		const subtitle = this.element(`.${TAG_NAME}__subtitle`);
		const yOffset: number = 20;
		eyebrow && gsap.set(eyebrow, { y: yOffset, opacity: 0 });
		header && gsap.set(header, { y: yOffset, opacity: 0 });
		subtitle && gsap.set(subtitle, { y: yOffset, opacity: 0 });
	}

	private animateElementsIn(): void {
		const eyebrow = this.element(`.${TAG_NAME}__eyebrow`);
		const header = this.element(`.${TAG_NAME}__header`);
		const subtitle = this.element(`.${TAG_NAME}__subtitle`);
		const easing = Cubic.easeOut;
		let startTime: number = 0.1;
		const tweenDuration: number = 0.7;
		if (eyebrow) {
			this.elementTweens.push(gsap.to(eyebrow, { ease: easing, duration: tweenDuration, y: 0, opacity: 1, delay: startTime }));
			startTime += 0.1;
		}
		if (header) {
			this.elementTweens.push(gsap.to(header, { ease: easing, duration: tweenDuration, y: 0, opacity: 1, delay: startTime }));
			startTime += 0.1;
		}
		if (subtitle) {
			this.elementTweens.push(gsap.to(subtitle, { ease: easing, duration: tweenDuration, y: 0, opacity: 1, delay: startTime }));
		}
	}

	private onScrollTriggerRefresh(): void {
		const useParallax: boolean = this.vars.get<boolean>(Attrs.parallax);
		if (useParallax) this.configureParallax();
	}

	private configureParallax(): void {
		let imageContainer: HTMLElement = this.element<HTMLElement>(`.${TAG_NAME}__image-container`);
		if (!imageContainer) return;
		const windowHeight: number = window.innerHeight;
		const imageContainerHeight: number = imageContainer.clientHeight;
		const targetParallaxType = imageContainerHeight > windowHeight ? ParallaxType.windowHeight : ParallaxType.imageHeight;
		this.parallaxType = targetParallaxType;
		this.parallaxTween?.scrollTrigger?.kill();
		this.parallaxTween?.kill();
		gsap.set(imageContainer, { y: 0 });
		switch (this.parallaxType) {
			case ParallaxType.windowHeight:
				//console.log('Configuring window-height parallax');
				this.configureWindowHeightParallax(imageContainer);
				break;
			case ParallaxType.imageHeight:
				//console.log('Configuring image-height parallax');
				this.configureImageHeightParallax(imageContainer);
				break;
		}
	}

	private configureWindowHeightParallax(imageContainer: HTMLElement): void {
		let distance = window.innerHeight * 0.2;
		// Create the tween first (paused)
		this.parallaxTween = gsap.to(imageContainer, {
			y: distance,
			ease: 'none',
			paused: true,
			scrollTrigger: {
				trigger: imageContainer,
				start: 'top top',
				end: 'bottom top',
				scrub: true,
			},
		});
	}

	private configureImageHeightParallax(imageContainer: HTMLElement): void {
		let distance = window.innerHeight * 0.15;
		let fps = 60;
		let interval = 1000 / fps;
		let lastTime = 0;

		// Create the tween first (paused)
		let parallaxTween = gsap.to(imageContainer, {
			y: distance,
			ease: 'none',
			paused: true,
		});

		// Now create the ScrollTrigger
		ScrollTrigger.create({
			trigger: imageContainer,
			start: 'top top',
			end: 'bottom top',
			scrub: true,
			onUpdate: (self) => {
				let now = performance.now();
				if (now - lastTime >= interval) {
					parallaxTween.progress(self.progress);
					lastTime = now;
				}
			},
		});
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.video?.destroy();
		window.removeEventListener('resize', this.resizeFunc);
		ScrollTrigger.removeEventListener('refresh', this.refreshFunc);
		this.parallaxType = null;
		this.elementTweens.forEach((tween) => tween.kill());
		this.elementTweens = [];
		this.parallaxTween?.kill();
		this.parallaxTween = null;
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.mediaType]: {
		description: 'Which type of media to display.',
		defaultValue: HHDSMastheadMediaType.image,
		typeString: 'HHDSMastheadMediaType',
		type: HHDSMastheadMediaType,
	},
	[Attrs.header]: {
		description: 'Header text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.eyebrow]: {
		description: 'Eyebrow text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.subtitle]: {
		description: 'Subtitle text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.showScrollButton]: {
		description: 'Whether to show the scroll button.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.mode]: {
		description: 'Image source.',
		defaultValue: HHDSMastheadColorMode.dark,
		type: HHDSMastheadColorMode,
		typeString: 'HHDSMastheadColorMode',
	},
	[Attrs.src]: {
		description: 'Media source.',
		defaultValue: '',
		type: String,
	},
	[Attrs.cover]: {
		description: 'URL to the video cover image.',
		defaultValue: '',
		type: String,
	},
	[Attrs.srcSet]: {
		description: 'Image srcset.',
		defaultValue: '',
		type: String,
	},
	[Attrs.sizes]: {
		description: 'Image sizes.',
		defaultValue: '',
		type: String,
	},
	[Attrs.alt]: {
		description: 'Image alt text.',
		defaultValue: '',
		type: String,
	},
	[Attrs.parallax]: {
		description: 'Whether to enable parallax scrolling.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.force16x9]: {
		description: 'Whether to force the media to be 16:9 aspect ratio.',
		defaultValue: false,
		type: Boolean,
	},
};
